import { PorQueEstePrecio } from './por-que-este-precio';
import { desdeFilaSimulada } from '../lib/recorrido-del-precio';
import type { FilaSimulada, RangoDeRegla, TipoDeLimite } from '../types/pricing-rules.types';

const r = (
  ruleId: string,
  ruleName: string,
  tipo: TipoDeLimite,
  min: number | null,
  max: number | null
): RangoDeRegla => ({
  ruleId,
  ruleName,
  tipo,
  duro: tipo === 'MARGEN_MINIMO',
  min,
  max,
  aviso: null,
});

const fila = (f: Partial<FilaSimulada>): FilaSimulada => ({
  productId: 'ejemplo',
  sku: 'EJEMPLO',
  name: 'Ejemplo',
  category: null,
  brand: null,
  precioHoy: 0,
  precioPedido: 0,
  origenDelPedido: 'ESTRATEGIA',
  estrategia: { id: 'ejemplo', name: 'Igualar al más barato' },
  usaMargenPropio: false,
  margenPropio: null,
  margenEstrategia: null,
  precioFinal: 0,
  precioPropuesto: 0,
  precioSinBorrador: null,
  rangos: [],
  cruce: { min: null, max: null, vacio: false },
  sinPrecioPosible: false,
  salidas: null,
  limitantes: [],
  avisos: [],
  redondeo: null,
  costo: null,
  comoPide: null,
  estado: 'LIBRE',
  ...f,
});

/**
 * TV Sony: costo 2.600, hoy 3.299, el más barato 3.199. Margen 20 % → desde
 * 3.250; moverse 15 % → 2.804,15 a 3.793,85; 10 % sobre el más barato → hasta
 * 3.518,90. Manda el margen de 20 %, y el redondeo lo termina en ,99.
 */
const TV = fila({
  precioHoy: 3299,
  precioPedido: 3199,
  comoPide: { metodo: 'MATCH_LOWEST', masBarato: 3199 },
  precioFinal: 3250.99,
  precioPropuesto: 3250.99,
  rangos: [
    r('m12', 'Margen mínimo 12 %', 'MARGEN_MINIMO', 2954.55, null),
    r('m20', 'Margen mínimo 20 %', 'MARGEN_MINIMO', 3250, null),
    r('mv', 'Moverse hasta 15 % por vez', 'MOVIMIENTO', 2804.15, 3793.85),
    r('mb', 'Hasta 10 % sobre el más barato', 'SOBRE_EL_MAS_BARATO', null, 3518.9),
  ],
  cruce: { min: 3250, max: 3518.9, vacio: false },
  limitantes: ['m20'],
  redondeo: { ruleId: 'r99', ruleName: 'Terminar en ,99', desde: 3250, hasta: 3250.99 },
  costo: 2600,
  estado: 'LIMITADO',
});

/**
 * JBL: el costo subió a 850, hoy 800, el más barato 780. Margen 12 % → desde
 * 965,91; moverse 15 % → 680 a 920; 10 % sobre el más barato → hasta 858. No
 * hay un precio que las cumpla todas. Las salidas vienen redondeadas como las
 * da el backend (965,99 · 857,99 · 800).
 */
const JBL = fila({
  name: 'Parlante JBL Flip 6',
  estrategia: { id: 'ejemplo', name: 'Quedar por debajo del más barato' },
  precioHoy: 800,
  precioPedido: 756.6,
  comoPide: { metodo: 'UNDERCUT_PERCENT', masBarato: 780, porcentaje: 3 },
  precioFinal: 965.99,
  precioPropuesto: 965.99,
  rangos: [
    r('m12', 'Margen mínimo 12 %', 'MARGEN_MINIMO', 965.91, null),
    r('mv', 'Moverse hasta 15 % por vez', 'MOVIMIENTO', 680, 920),
    r('mb', 'Hasta 10 % sobre el más barato', 'SOBRE_EL_MAS_BARATO', null, 858),
  ],
  cruce: { min: 965.91, max: 858, vacio: true },
  sinPrecioPosible: true,
  salidas: [
    {
      clave: 'MARGEN',
      precio: 965.99,
      explicacion: '',
      respeta: ['m12'],
      rompe: ['mv', 'mb'],
    },
    {
      clave: 'OTRAS',
      precio: 857.99,
      explicacion: '',
      respeta: ['mv', 'mb'],
      rompe: ['m12'],
    },
    {
      clave: 'NO_MOVER',
      precio: 800,
      explicacion: '',
      respeta: ['mv', 'mb'],
      rompe: ['m12'],
    },
  ],
  costo: 850,
  estado: 'SIN_PRECIO_POSIBLE',
});

const ALCANCE = {
  m12: 'todo el catálogo',
  m20: 'marca Sony',
  mv: 'todo el catálogo',
  mb: 'todo el catálogo',
};

/**
 * El ejemplo de la ayuda (spec 225): el mismo dibujo que la pantalla, armado
 * con el mismo adaptador (`desdeFilaSimulada`) y números fijos. No es una
 * simulación, es la explicación; la de verdad está en «Cómo está cada producto».
 */
export function DibujoExplicativo() {
  return (
    <div className="grid grid-cols-2 gap-6">
      <div>
        <div className="mb-1 text-sm font-medium">
          Televisor Sony 55" 4K · las reglas se cumplen
        </div>
        <PorQueEstePrecio
          recorrido={desdeFilaSimulada(TV)}
          tiempo="HOY"
          alcancePorRegla={ALCANCE}
        />
      </div>
      <div>
        <div className="mb-1 text-sm font-medium">Parlante JBL Flip 6 · conflicto entre reglas</div>
        <PorQueEstePrecio
          recorrido={desdeFilaSimulada(JBL)}
          tiempo="HOY"
          alcancePorRegla={{ ...ALCANCE, mb: 'categoría Audio' }}
        />
      </div>
    </div>
  );
}
